/**
 * Auth context — exposes the current Cognito session + sign-in/out helpers.
 * Wraps `aws-amplify/auth` so the rest of the app never imports Amplify directly.
 */
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
  getCurrentUser,
  confirmSignIn,
  setUpTOTP,
  verifyTOTPSetup,
  resetPassword,
  confirmResetPassword,
  type SignInOutput,
} from 'aws-amplify/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { jwtClaimsSchema } from '@fin/shared/schemas';
import { STAFF_GROUPS, type CognitoGroup } from '@fin/shared/enums';

export interface AuthUser {
  userId: string;
  email: string;
  groups: CognitoGroup[];
  givenName?: string;
  familyName?: string;
}

export type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

export interface SignInChallenge {
  /** Cognito challenge name (e.g. NEW_PASSWORD_REQUIRED, SOFTWARE_TOKEN_MFA, MFA_SETUP). */
  challenge: SignInOutput['nextStep']['signInStep'];
  /** TOTP secret (only present when challenge === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP'). */
  totpSetupUri?: string;
}

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** Returns either the resolved user, or a challenge that the UI must complete. */
  signIn: (email: string, password: string) => Promise<SignInChallenge | { user: AuthUser }>;
  /** Continue an in-progress sign-in flow (TOTP code, new password, etc.). */
  respondToChallenge: (response: string) => Promise<SignInChallenge | { user: AuthUser }>;
  /** Verify a TOTP code typed by the user against their newly-issued secret. */
  verifyTotp: (code: string) => Promise<void>;
  /** Start a forgot-password flow — Cognito emails a 6-digit code to the user. */
  requestPasswordReset: (email: string) => Promise<void>;
  /** Complete a forgot-password flow with the emailed code + new password. */
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Get the current ID token (used by the API client). */
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function loadCurrentUser(): Promise<AuthUser | null> {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    if (!idToken) return null;
    const claims = jwtClaimsSchema.parse(idToken.payload);
    const current = await getCurrentUser();
    return {
      userId: claims.sub,
      email: claims.email ?? current.signInDetails?.loginId ?? '',
      groups: [...claims['cognito:groups']] as CognitoGroup[],
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    void loadCurrentUser().then((u) => {
      setUser(u);
      setStatus(u ? 'authenticated' : 'unauthenticated');
    });
  }, []);

  const refresh = useCallback(async () => {
    const u = await loadCurrentUser();
    setUser(u);
    setStatus(u ? 'authenticated' : 'unauthenticated');
    return u;
  }, []);

  const handleStep = useCallback(
    async (output: SignInOutput): Promise<SignInChallenge | { user: AuthUser }> => {
      const step = output.nextStep.signInStep;
      if (step === 'DONE') {
        const u = await refresh();
        if (!u) throw new Error('Sign-in completed but no session was created.');
        return { user: u };
      }
      if (step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        const setup = output.nextStep as { totpSetupDetails: { getSetupUri: (n: string, a?: string) => URL } };
        const uri = setup.totpSetupDetails.getSetupUri('FixItNinjaz Admin').toString();
        return { challenge: step, totpSetupUri: uri };
      }
      return { challenge: step };
    },
    [refresh],
  );

  const signIn = useCallback<AuthContextValue['signIn']>(
    async (email, password) => {
      // Amplify v6 throws `UserAlreadyAuthenticatedException` if a session is
      // already cached in localStorage. Clear it first so re-login always works.
      try {
        await amplifySignOut();
      } catch {
        /* nothing to sign out */
      }
      const output = await amplifySignIn({
        username: email.trim().toLowerCase(),
        password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' },
      });
      return handleStep(output);
    },
    [handleStep],
  );

  const respondToChallenge = useCallback<AuthContextValue['respondToChallenge']>(
    async (response) => {
      const output = await confirmSignIn({ challengeResponse: response });
      return handleStep(output);
    },
    [handleStep],
  );

  const verifyTotp = useCallback<AuthContextValue['verifyTotp']>(async (code) => {
    await verifyTOTPSetup({ code });
  }, []);

  const requestPasswordReset = useCallback<AuthContextValue['requestPasswordReset']>(async (email) => {
    await resetPassword({ username: email.trim().toLowerCase() });
  }, []);

  const confirmPasswordReset = useCallback<AuthContextValue['confirmPasswordReset']>(
    async (email, code, newPassword) => {
      await confirmResetPassword({
        username: email.trim().toLowerCase(),
        confirmationCode: code.trim(),
        newPassword,
      });
    },
    [],
  );

  const signOut = useCallback<AuthContextValue['signOut']>(async () => {
    await amplifySignOut();
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const getIdToken = useCallback<AuthContextValue['getIdToken']>(async () => {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  }, []);

  // Keep `setUpTOTP` callable from outside so a user can opt-in to MFA after first login.
  // (Not exposed via context in v1 — admin policy is "set up TOTP at first login".)
  void setUpTOTP;

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      signIn,
      respondToChallenge,
      verifyTotp,
      requestPasswordReset,
      confirmPasswordReset,
      signOut,
      getIdToken,
    }),
    [
      status,
      user,
      signIn,
      respondToChallenge,
      verifyTotp,
      requestPasswordReset,
      confirmPasswordReset,
      signOut,
      getIdToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}

/** Where a signed-in user lands. Marketing-only users have no CRM pages, so they start on Content. */
export function homePathFor(groups: readonly CognitoGroup[] | undefined): string {
  return groups?.some((g) => STAFF_GROUPS.includes(g)) ? '/' : '/content';
}
